/** Explicit cabinet context, isolated per browser tab. Server memberships remain authoritative. */
const KEY = "kollectivia.tenant_scope";
let current: string | null = (() => {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
})();
let generation = 0;
const listeners = new Set<(tenantId: string | null) => void>();
const requests = new Map<AbortController, boolean>();
export const tenantScope = () => current;
export const tenantScopeGeneration = () => generation;
export const tenantScopeHeader = (): Record<string, string> =>
  current ? { "x-tenant-id": current } : {};
export const hasTenantWrites = () => [...requests.values()].some(Boolean);
function applyScope(tenantId: string | null) {
  generation += 1;
  for (const controller of requests.keys()) controller.abort();
  current = tenantId;
  try {
    if (tenantId === null) window.sessionStorage.removeItem(KEY);
    else window.sessionStorage.setItem(KEY, tenantId);
  } catch {
    /* Session storage unavailable: keep this tab's in-memory scope. */
  }
  for (const listener of listeners) listener(tenantId);
}
export function setTenantScope(tenantId: string | null): boolean {
  if (tenantId === current) return true;
  if (hasTenantWrites()) return false;
  applyScope(tenantId);
  return true;
}
/** Server denial invalidates cached data without silently selecting another cabinet. */
export function invalidateTenantScope(): void {
  applyScope(current);
}
/** Logout / identity change invalidates even same-tenant responses from the old session. */
export function resetTenantScope(): void {
  applyScope(null);
}
export function onTenantScopeChange(fn: (tenantId: string | null) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
export function beginTenantRequest(writing: boolean) {
  const epoch = generation;
  const controller = new AbortController();
  requests.set(controller, writing);
  return {
    headers: tenantScopeHeader(),
    signal: controller.signal,
    assertCurrent() {
      if (epoch !== generation || controller.signal.aborted)
        throw new DOMException("Cabinet context changed", "AbortError");
    },
    abort() {
      controller.abort();
    },
    finish() {
      requests.delete(controller);
    },
  };
}
